import React from 'react'
import { Col, Row } from 'antd'
import styles from './index.module.scss'
import CustomInput from './custom-inputs/custom-input/CustomInput'

type IProps = {
  fields: any
  form: any
  disabled?: boolean
  customInputs?: boolean
  fieldsPerRow?: number
  prefixTranslate?: string
  gutter?: number
  withSections?: boolean
  translatePath?: string
  autoCalculateColSpan?: boolean
}

const maxColSpan = 24

const RenderForm: React.FC<IProps> = ({ form, fields, disabled = false, customInputs = false, fieldsPerRow = 1, gutter = 20, withSections = false, autoCalculateColSpan = true, prefixTranslate }) => {
  // const { countryTranslate } = useCountryTranslate()
  const [keys, setKeys] = React.useState<string[]>([])

  React.useEffect(() => {
    if (customInputs && !withSections) {
      setKeys(fields?.map((field: any) => field.name) || [])
    } else if (customInputs && withSections) {
      const fieldsKeys = [] as any

      Object.keys(fields)?.forEach((section) => {
        if (Array.isArray(fields[section])) {
          fields[section]?.map((field: any) => fieldsKeys.push(field?.name))
        } else if (typeof fields[section] === 'object' && fields[section] !== null) {
          fieldsKeys.push(fields[section]?.name)
        }
      })

      setKeys(fieldsKeys)
    } else if (!customInputs) {
      setKeys(fields ? Object.keys(fields) : [])
    }
  }, [customInputs, withSections, fields])

  const renderFieldsBasic = (input: any, gutter = 25) => {
    const inputs = typeof input === 'function' ? { fields: input } : input
    const cantFields = Object.keys(inputs).length
    const colSpan = 24 / cantFields

    return (
      <Row gutter={cantFields > 1 ? gutter : 0}>
        {Object.keys(inputs)?.map((key) => {
          return (
            <Col key={`col-render-form-${key}`} span={colSpan}>
              {inputs[key]({
                form,
                disabled,
              })}
            </Col>
          )
        })}
      </Row>
    )
  }

  function paginate(fields: any) {
    return fields.reduce((acc: any, field: any, index: any) => {
      const idx = Math.floor(index / fieldsPerRow)
      const page = acc[idx] || (acc[idx] = [])
      page.push(field)

      return acc
    }, [])
  }

  const setMaxColSpan = (fields: any, calculatedColSpan: any) => {
    fields.map((field: any) => {
      if (field?.colSpan) {
        field.colSpan = field.colSpan > maxColSpan ? maxColSpan : field.colSpan
        field.colSpan = field.colSpan < calculatedColSpan ? calculatedColSpan : field.colSpan
      } else {
        field.colSpan = calculatedColSpan
      }
    })
  }

  const renderCustomFields = (_fields: any, _totalRows?: number) => {
    const calculatedColSpan = 24 / fieldsPerRow
    autoCalculateColSpan && setMaxColSpan(_fields, calculatedColSpan)
    const totalRows = !_totalRows ? Math.ceil(Object.keys(fields)?.length / fieldsPerRow) : _totalRows
    const pages = paginate(_fields)

    return Array(totalRows)
      .fill('rowItem')
      .map((_, index) => {
        return (
          <Row gutter={fieldsPerRow > 1 ? gutter : 0} key={index}>
            {pages[index]?.map(({ type, ...field }: any, i: number) => {
              const props: any = {
                form,
                name: field?.name,
                ...field,
                label: field?.label || (field.name ? `${prefixTranslate}.fields.${field.name}.label` : ''),
              }

              return (
                <Col hidden={field.hidden} key={`${index}-${i}`} span={field.colSpan || calculatedColSpan}>
                  <CustomInput props={props} type={type} />
                </Col>
              )
            })}
          </Row>
        )
      })
  }

  const renderCustomFieldsWithSections = () => {
    return Object.keys(fields).map((section) => {
      const sectionContent = fields[section]
      const isSectionEmpty = Array.isArray(sectionContent) && sectionContent.length === 0
      const isSectionObjectEmpty = typeof sectionContent === 'object' && Object.keys(sectionContent).length === 0
      if (isSectionEmpty || isSectionObjectEmpty) return null

      if (Array.isArray(fields[section])) {
        const totalItems = Object.keys(fields[section]).length
        const totalRows = Math.ceil(totalItems / fieldsPerRow)

        return (
          fields[section]?.length && (
            <React.Fragment key={section}>
              <p className={styles.sectionTitle}>{`${prefixTranslate}.sections.${section}`}</p>
              {renderCustomFields(fields[section], totalRows)}
            </React.Fragment>
          )
        )
      }

      return typeof fields[section] === 'object' && fields[section] !== null ? (
        <React.Fragment key={section}>
          <p className={styles.sectionTitle}>
            {`${prefixTranslate}.sections.${section}`}

            <div className={styles.sectionActions} id={`ActionsRenderForm-${fields[section].name}`} />
          </p>
          <Row>
            <Col>
              {fields[section].render &&
                fields[section].render({
                  form,
                  name: fields[section].name,
                })}
            </Col>
          </Row>
        </React.Fragment>
      ) : null
    }) as React.ReactNode
  }

  const renderFields = () => {
    if (customInputs) {
      if (withSections) {
        return renderCustomFieldsWithSections()
      } else {
        return renderCustomFields(fields)
      }
    }

    return keys.map((field) => (fields && fields[field] ? renderFieldsBasic(fields[field]) : null))
  }

  return renderFields() as React.ReactElement
}

export default RenderForm
